import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import includes from 'lodash/includes';
import pull from 'lodash/pull';
import { useState } from 'react';
import { useController } from 'react-hook-form';
import { customTheme } from '../../theme';
import { getPriceWithCurrency } from '../../utils/charges';
import { CheckboxOptionProps } from './types';
import { useTranslation } from 'react-i18next';

export function CheckboxOptions(props: CheckboxOptionProps) {
  const controller = useController({ name: props.name });
  const { field } = controller;
  const {
    options,
    itemCount,
    minimumSelections,
    maximumSelections,
    type,
    error,
  } = props;
  const [checkedOptions, setCheckedOptions] = useState<string[]>([]);
  const { t } = useTranslation(['patron_portal', 'common']);
  const helperText = props.helperText;
  const isMandatory = type === 'mandatory';
  const allMandatoryOptionsNotSelected =
    isMandatory && checkedOptions.length < (minimumSelections || 0);

  const handleCheck = (checkedId: string) => {
    const ids = includes(checkedOptions, checkedId)
      ? pull([...checkedOptions], checkedId)
      : [...checkedOptions, checkedId];

    if (
      (type === 'optional' || type === 'mandatory') &&
      maximumSelections !== undefined &&
      ids.length > maximumSelections
    ) {
      return checkedOptions;
    }

    setCheckedOptions(ids);
    field.onChange(ids);
    return ids;
  };
  const showHelperText = error && type === 'mandatory';
  const backgroundColor = allMandatoryOptionsNotSelected
    ? '#954104ff'
    : '#03845bff';

  return (
    <FormGroup>
      {showHelperText && (
        <Typography variant="helperText" color={backgroundColor}>
          {helperText}
        </Typography>
      )}
      {options.map((option, index) => (
        <Box key={option._id}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 2,
              px: 1,
            }}
          >
            <Box sx={{ display: 'flex', flexGrow: 1 }}>
              <Typography variant="optionName">{option.name}</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              {option.price !== 0 && (
                <Typography sx={{ mt: 0.5 }}>
                  {getPriceWithCurrency(option.markupPrice)}
                  {checkedOptions.includes(option._id) && itemCount > 1 && (
                    <span style={{ marginLeft: '4px' }}>(×{itemCount})</span>
                  )}
                </Typography>
              )}
              <Checkbox
                checked={includes(checkedOptions, option._id)}
                onChange={() => field.onChange(handleCheck(option._id))}
                sx={{
                  padding: 0,
                  ml: 2,
                  color: showHelperText ? '#cfceceff' : 'black',
                  '&.Mui-checked': {
                    color: 'black',
                  },
                }}
                disabled={
                  (type === 'optional' || type === 'mandatory') &&
                  maximumSelections !== undefined &&
                  checkedOptions.length >= maximumSelections &&
                  !checkedOptions.includes(option._id)
                }
              />
            </Box>
          </Box>

          {index !== options.length - 1 && (
            <Divider sx={{ borderColor: '#e9e4e4ff' }} />
          )}
        </Box>
      ))}
    </FormGroup>
  );
}
